// frontend/src/services/customers.ts
//
// 客户与卡库(Vault)服务（商户 JWT 组 /merchant/customers...）。
// 卡片均为脱敏信息（pm_<uuid> + brand/last4/有效期），永远拿不到完整卡号。

import ApiService from './api';
import type { ListMeta } from './catalog';
import { safeArray } from '../utils/money';

export interface Customer {
  id: string;
  email: string;
  name?: string;
  description?: string;
  phone?: string;
  default_payment_method_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CustomerPaymentMethod {
  id: string;
  customer_id: string;
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  funding?: string;
  is_default: boolean;
  provider?: string;
  created_at: string;
}

export interface CustomerRequest {
  email: string;
  name?: string;
  description?: string;
  phone?: string;
  metadata?: Record<string, any>;
}

const DEFAULT_META: ListMeta = { total: 0, per_page: 20, current_page: 1, last_page: 1 };

export class CustomerService {
  /** 创建客户 */
  static async createCustomer(data: CustomerRequest): Promise<Customer> {
    const response = await ApiService.post<Customer>('/merchant/customers', data);
    return response.data;
  }

  /** 客户列表 */
  static async listCustomers(
    page: number = 1,
    perPage: number = 20
  ): Promise<{ data: Customer[]; meta: ListMeta }> {
    const response = await ApiService.get<Customer[]>('/merchant/customers', {
      page,
      per_page: perPage,
    });
    return { data: safeArray<Customer>(response.data), meta: (response.meta as ListMeta) || DEFAULT_META };
  }

  /** 客户详情 */
  static async getCustomer(id: string): Promise<Customer> {
    const response = await ApiService.get<Customer>(`/merchant/customers/${id}`);
    return response.data;
  }

  /** 更新客户 */
  static async updateCustomer(id: string, data: Partial<CustomerRequest>): Promise<Customer> {
    const response = await ApiService.put<Customer>(`/merchant/customers/${id}`, data);
    return response.data;
  }

  /** 删除客户 */
  static async deleteCustomer(id: string): Promise<void> {
    await ApiService.delete(`/merchant/customers/${id}`);
  }

  /** 客户已存卡列表（脱敏） */
  static async listPaymentMethods(customerId: string): Promise<CustomerPaymentMethod[]> {
    const response = await ApiService.get<CustomerPaymentMethod[]>(
      `/merchant/customers/${customerId}/payment-methods`
    );
    return safeArray<CustomerPaymentMethod>(response.data);
  }

  /** 设为默认支付方式 */
  static async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    await ApiService.post(`/merchant/payment-methods/${paymentMethodId}/default`);
  }

  /** 解绑支付方式 */
  static async detachPaymentMethod(paymentMethodId: string): Promise<void> {
    await ApiService.delete(`/merchant/payment-methods/${paymentMethodId}`);
  }
}

export default CustomerService;
